/**
 * SEO content + canonical URL helpers.
 *
 * Central source of truth for:
 *  - Country slugs under /singapore-visa/{slug}
 *  - The 8 guide-page canonical paths + on-page copy
 *  - The 29 target nationalities from the SEO brief
 *  - Per-nationality LOI status verified against ICA on 2026-02-13
 *
 * All content stays factual — no fake ratings, no fake affiliations, no
 * "guaranteed approval" language.
 */

/**
 * The 29 target nationalities for the SEO directory + country pages,
 * using SEO-friendly slugs. `code` matches the CMS nationality code in
 * mock.js so we can look up prices and application-enabled from the live
 * CMS data. `loi` and `exemption` are verified against the ICA country
 * pages on 2026-02-13 and are the SINGLE SOURCE OF TRUTH for LOI wording
 * across every surface (React render, JSON-LD, pre-rendered HTML).
 *
 * LOI states:
 *   'required'      — ICA country page lists Form V39A as a required doc
 *   'case-by-case'  — ICA country page mentions Form V39A only under
 *                     "Additional supporting documents ... may be required"
 *   'not-required'  — ICA country page states the nationality does not
 *                     require a visa (no supported country matches today)
 *
 * NOTE: Democratic People's Republic of Korea (kp) is intentionally
 * excluded from this list. VisaSG does not currently offer Singapore
 * visa application assistance for North Korean nationals; keeping the
 * entry out of SEO_COUNTRIES guarantees no directory entry, no hub
 * preview card, no /singapore-visa/north-korea/ route, no sitemap entry,
 * and no crawlable internal link is ever generated for that nationality.
 */
export const SEO_COUNTRIES = [
  { code: 'af', slug: 'afghanistan',   name: 'Afghanistan',   demonym: 'Afghan',         loi: 'required',     exemption: null },
  { code: 'dz', slug: 'algeria',       name: 'Algeria',       demonym: 'Algerian',       loi: 'required',     exemption: null },
  { code: 'am', slug: 'armenia',       name: 'Armenia',       demonym: 'Armenian',       loi: 'case-by-case', exemption: 'diplomatic' },
  { code: 'az', slug: 'azerbaijan',    name: 'Azerbaijan',    demonym: 'Azerbaijani',    loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'by', slug: 'belarus',       name: 'Belarus',       demonym: 'Belarusian',     loi: 'case-by-case', exemption: 'diplomatic-official' },
  { code: 'eg', slug: 'egypt',         name: 'Egypt',         demonym: 'Egyptian',       loi: 'required',     exemption: 'diplomatic' },
  { code: 'ge', slug: 'georgia',       name: 'Georgia',       demonym: 'Georgian',       loi: 'case-by-case', exemption: 'diplomatic-service' },
  { code: 'in', slug: 'india',         name: 'India',         demonym: 'Indian',         loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'jo', slug: 'jordan',        name: 'Jordan',        demonym: 'Jordanian',      loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'kz', slug: 'kazakhstan',    name: 'Kazakhstan',    demonym: 'Kazakh',         loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'xk', slug: 'kosovo',        name: 'Kosovo',        demonym: 'Kosovar',        loi: 'required',     exemption: 'diplomatic-official' },
  { code: 'kg', slug: 'kyrgyzstan',    name: 'Kyrgyzstan',    demonym: 'Kyrgyz',         loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'lb', slug: 'lebanon',       name: 'Lebanon',       demonym: 'Lebanese',       loi: 'required',     exemption: null },
  { code: 'ly', slug: 'libya',         name: 'Libya',         demonym: 'Libyan',         loi: 'required',     exemption: null },
  { code: 'ml', slug: 'mali',          name: 'Mali',          demonym: 'Malian',         loi: 'required',     exemption: null },
  { code: 'md', slug: 'moldova',       name: 'Moldova',       demonym: 'Moldovan',       loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'ma', slug: 'morocco',       name: 'Morocco',       demonym: 'Moroccan',       loi: 'required',     exemption: 'diplomatic' },
  { code: 'ng', slug: 'nigeria',       name: 'Nigeria',       demonym: 'Nigerian',       loi: 'required',     exemption: 'diplomatic-official-service' },
  { code: 'pk', slug: 'pakistan',      name: 'Pakistan',      demonym: 'Pakistani',      loi: 'required',     exemption: null },
  { code: 'ru', slug: 'russia',        name: 'Russia',        demonym: 'Russian',        loi: 'case-by-case', exemption: 'diplomatic-service' },
  { code: 'so', slug: 'somalia',       name: 'Somalia',       demonym: 'Somali',         loi: 'required',     exemption: null },
  { code: 'ss', slug: 'south-sudan',   name: 'South Sudan',   demonym: 'South Sudanese', loi: 'required',     exemption: null },
  { code: 'sd', slug: 'sudan',         name: 'Sudan',         demonym: 'Sudanese',       loi: 'required',     exemption: null },
  { code: 'tj', slug: 'tajikistan',    name: 'Tajikistan',    demonym: 'Tajik',          loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'tn', slug: 'tunisia',       name: 'Tunisia',       demonym: 'Tunisian',       loi: 'required',     exemption: 'diplomatic-special' },
  { code: 'tm', slug: 'turkmenistan',  name: 'Turkmenistan',  demonym: 'Turkmen',        loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'ua', slug: 'ukraine',       name: 'Ukraine',       demonym: 'Ukrainian',      loi: 'case-by-case', exemption: 'diplomatic-official-service' },
  { code: 'uz', slug: 'uzbekistan',    name: 'Uzbekistan',    demonym: 'Uzbek',          loi: 'case-by-case', exemption: 'diplomatic' },
  { code: 'ye', slug: 'yemen',         name: 'Yemen',         demonym: 'Yemeni',         loi: 'required',     exemption: null },
];

export const SEO_COUNTRY_BY_SLUG = SEO_COUNTRIES.reduce((acc, c) => {
  acc[c.slug] = c;
  return acc;
}, {});

export const SEO_COUNTRY_BY_CODE = SEO_COUNTRIES.reduce((acc, c) => {
  acc[c.code] = c;
  return acc;
}, {});

/** Legacy slug (used by the older /singapore-visa-for/<name-slug> pages) →
 *  new slug, for 301-style redirects. */
export const LEGACY_SLUG_TO_NEW = SEO_COUNTRIES.reduce((acc, c) => {
  const nameSlug = c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  acc[nameSlug] = c.slug;
  return acc;
}, {});

/**
 * Informational LOI wording per state. Waan Travel Pte. Ltd. does NOT
 * issue Form V39A / Letters of Introduction as a commercial visa
 * service and does NOT act as a commercial local Singapore sponsor.
 */
export const LOI_WORDING = {
  'required':     'A Letter of Introduction (Form V39A) is a standard requirement listed by ICA for this nationality. Applicants must obtain the LOI from an eligible local contact in Singapore per ICA guidance. Waan Travel Pte. Ltd. does not issue LOIs as a commercial visa service.',
  'case-by-case': 'ICA may request a Letter of Introduction (Form V39A) on a case-by-case basis. Applicants should refer to the current ICA country page and, if required, obtain the LOI from an eligible local contact per ICA guidance. Waan Travel Pte. Ltd. does not issue LOIs as a commercial visa service.',
  'not-required': 'A Letter of Introduction (Form V39A) is not a standard requirement for this nationality.',
  'unknown':      'LOI requirements depend on nationality and ICA guidance. Please check the current ICA country page for your nationality.',
};

/** Short label for compact stat cards / table rows. */
export const LOI_LABEL = {
  'required':     'Standard requirement (per ICA)',
  'case-by-case': 'May be requested by ICA',
  'not-required': 'Not required',
  'unknown':      'Refer to ICA country page',
};

/**
 * Passport-exemption note per official ICA country page. Nationality-
 * specific because ICA lists different exemption sets per country and
 * some (Afghanistan, Lebanon, Libya, Mali, Pakistan, Somalia, South
 * Sudan, Sudan, Yemen) list no such exemption.
 */
export const EXEMPTION_WORDING = {
  'diplomatic':                     'ICA notes that holders of diplomatic passports issued by this country do not need a visa for entry.',
  'diplomatic-official':            'ICA notes that holders of diplomatic or official passports issued by this country do not need a visa for entry.',
  'diplomatic-service':             'ICA notes that holders of diplomatic or service passports issued by this country do not need a visa for entry.',
  'diplomatic-official-service':    'ICA notes that holders of diplomatic, official or service passports issued by this country do not need a visa for entry.',
  'diplomatic-special':             'ICA notes that holders of diplomatic or special passports issued by this country do not need a visa for entry.',
};

// --- Build-time integrity check ------------------------------------
// This runs at module-load time so `react-scripts build` (and every
// dev-server refresh) fails if a future edit removes an LOI state or
// introduces a value we don't have wording for. Also protects against
// LOI counts drifting away from the 2026-02-13 ICA verification.
(function validateSeoCountries() {
  const valid = new Set(['required', 'case-by-case', 'not-required']);
  const missing = [];
  const invalid = [];
  const badExemption = [];
  for (const c of SEO_COUNTRIES) {
    if (!c.loi) missing.push(c.code);
    else if (!valid.has(c.loi)) invalid.push(`${c.code}=${c.loi}`);
    if (c.exemption && !EXEMPTION_WORDING[c.exemption]) badExemption.push(`${c.code}=${c.exemption}`);
  }
  if (missing.length) {
    throw new Error(`[seo-content] SEO_COUNTRIES missing LOI state: ${missing.join(', ')}`);
  }
  if (invalid.length) {
    throw new Error(`[seo-content] SEO_COUNTRIES invalid LOI state: ${invalid.join(', ')}`);
  }
  if (badExemption.length) {
    throw new Error(`[seo-content] SEO_COUNTRIES exemption without wording: ${badExemption.join(', ')}`);
  }
})();

/**
 * Guide pages under /singapore-visa/{path}. `path` is the URL segment,
 * e.g. "requirements". `title` = SEO title. `h1` = visible H1. `desc` =
 * meta description. `sections` = ordered array of {heading, body} to
 * render on the page — `body` may contain plain text or React-safe
 * markdown-like strings with `[link text](/some/path)` internal links
 * (rendered by the SingaporeVisaGuide component).
 *
 * Wording verified against official ICA / MFA sources on 2026-02-13.
 */
export const SEO_GUIDES = [
  {
    path: 'requirements',
    title: 'Singapore Visa Requirements — Documents, Passport & Photo | VisaSG',
    h1: 'Singapore Visa Requirements',
    desc: 'Singapore visa requirements explained: who needs one, required documents, passport rules and photo specifications. Prepare before you apply with VisaSG.',
    intro:
      'Whether you need a Singapore visa before travel depends on the passport you hold. Passport holders whose nationality is not covered by visa-free arrangements must obtain a Singapore entry visa. A Singapore entry visa is pre-entry permission to travel to and seek entry into Singapore; it does not itself guarantee entry, which is determined by ICA officers at the point of entry.',
    sections: [
      {
        heading: 'Who Needs a Singapore Visa?',
        body:
          'The current list of visa-required nationalities is maintained by ICA. Check the [ICA visa-required nationalities list](https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements) or see [Singapore visa requirements by nationality](/singapore-visa/countries/) for the nationalities VisaSG currently supports.',
      },
      {
        heading: 'Core Documents ICA Requires',
        body:
          'ICA lists three core documents for an entry-visa application on Form 14A: a completed and signed Form 14A, a recent passport-sized colour photograph (see [photo requirements](/singapore-visa/photo-requirements/)), and a photocopy of the passport bio-data page. Additional supporting documents, including Form V39A Letter of Introduction (LOI), may be required by ICA on a case-by-case basis, or as a standard requirement for certain nationalities. Full checklist: [Singapore visa documents required](/singapore-visa/documents/).',
      },
      {
        heading: 'Passport Validity',
        body:
          'ICA requires all travellers, except Singapore passport holders, to hold a passport with a minimum validity of six months. Details: [Singapore visa passport requirements](/singapore-visa/passport-requirements/).',
      },
      {
        heading: 'Photograph',
        body:
          'A recent passport-sized colour photograph taken within the last three months, per ICA photo guidelines. See [Singapore visa photo requirements](/singapore-visa/photo-requirements/).',
      },
      {
        heading: 'Fees and Processing',
        body:
          'VisaSG uses one all-in USD price. The total includes the ICA government fee (remitted to ICA on your behalf), the VisaSG service fee, and the Letter of Introduction where required. See [Singapore visa fees & cost](/singapore-visa/fees/) · [Singapore visa processing time](/singapore-visa/processing-time/).',
      },
      {
        heading: 'How to Apply',
        body:
          'Complete the online application, upload your documents, pay in USD, and track your eVisa status. Step-by-step: [How to apply for a Singapore visa](/singapore-visa/how-to-apply/).',
      },
    ],
  },
  {
    path: 'documents',
    title: 'Singapore Visa Documents Required — Full Checklist | VisaSG',
    h1: 'Singapore Visa Documents Required',
    desc: 'Full checklist of Singapore visa documents ICA lists on its country pages: passport bio-data, passport-style photograph, and supporting documents ICA may request.',
    intro:
      'Below is a summary of the documents ICA lists on its country pages for entry-visa applications. VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers.',
    sections: [
      {
        heading: 'Documents ICA Lists for the Visa Application',
        body:
          'Per ICA country pages, the documents typically listed for an entry-visa application are:\n1. A duly completed and signed [Form 14A](https://www.ica.gov.sg/docs/default-source/ica/forms/form14a.pdf).\n2. A recent passport-sized colour photograph taken within the last three months (see [photo requirements](/singapore-visa/photo-requirements/)).\n3. A photocopy of your passport bio-data page. Your passport must be valid for at least six months from the date of your entry into Singapore.\n4. Where applicable per ICA guidance, a completed [Form V39A Letter of Introduction](https://www.ica.gov.sg/docs/default-source/ica/forms/formv39a.pdf) issued by an eligible local contact in Singapore. Whether an LOI is a standard or case-by-case requirement varies by nationality — see [Singapore visa requirements by nationality](/singapore-visa/countries/). Documents in languages other than English must be accompanied by an official English translation. ICA may request additional supporting documents on a case-by-case basis.',
      },
      {
        heading: 'How to Apply',
        body:
          'Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by ICA. Visa applications are assessed by ICA on their individual merits. Refer to the [official ICA visa requirements page](https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements).',
      },
      {
        heading: 'Waan Travel Pte. Ltd.',
        body:
          'Waan Travel Pte. Ltd. is a Singapore-licensed travel agency (STB Travel Agent Licence No. TA03795). We do not submit Singapore visa applications on behalf of customers and do not issue Form V39A or Letters of Introduction as a commercial visa service. We can arrange legitimate travel services separately — hotel booking, flight ticket booking, tour packages, transportation and travel itinerary planning.',
      },
    ],
  },
  {
    path: 'fees',
    title: 'Singapore Visa Fee — Prescribed ICA Fee (S$30) | VisaSG',
    h1: 'Singapore Visa Fee',
    desc: 'The prescribed ICA visa processing fee is S$30 per application, payable to ICA through the appropriate channel prescribed by ICA.',
    intro:
      'The prescribed ICA visa processing fee is S$30 per application. This fee is payable to ICA through the appropriate channel prescribed by ICA and is not payable to Waan Travel Pte. Ltd.',
    sections: [
      {
        heading: 'Prescribed ICA Visa Processing Fee',
        body:
          'The prescribed ICA visa processing fee is S$30 per application. Please refer to the [official ICA visa requirements page](https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements) for the current fee and payment channel.',
      },
      {
        heading: 'Waan Travel Pte. Ltd. — Travel Services',
        body:
          'Fees for Waan Travel Pte. Ltd. travel services (hotel booking, flight ticket booking, tour packages, transportation arrangements, travel itinerary planning) are quoted separately on request. They are not Singapore visa application or processing fees and are not payable to ICA.',
      },
      {
        heading: 'SG Arrival Card (SGAC)',
        body:
          "The SG Arrival Card is not a Singapore visa. SGAC submission via the [official ICA SGAC e-Service](https://www.ica.gov.sg/eservicesandforms/sgarrivalcard) is free of charge.",
      },
    ],
  },
  {
    path: 'processing-time',
    title: 'Singapore Visa Processing Time — ICA Turnaround | VisaSG',
    h1: 'Singapore Visa Processing Time',
    desc: 'How long a Singapore visa takes per ICA guidance. Each application is considered on its own merits and processing times are not guaranteed.',
    intro:
      'ICA states that visa applications are processed within three working days, excluding the day of submission. Some applications may take longer to process. Each visa application is considered on its own merits.',
    sections: [
      {
        heading: 'Typical Processing Timeline (per ICA)',
        body:
          'ICA states that visa applications are processed within three working days (excluding the day of submission). Some applications may take longer. This is the wording used on ICA country pages — see for example [Singapore visa for Indian passport holders (ICA)](https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements/visa-detail-page/india).',
      },
      {
        heading: 'Peak Periods and Public Holidays',
        body:
          'Applications submitted close to Singapore public holidays or during peak travel periods may take longer to process. ICA advises applying within 30 days prior to your intended arrival in Singapore.',
      },
      {
        heading: 'Where to Check the Status of an Application',
        body:
          'Applicants who applied through the appropriate channel prescribed by ICA can check their status via the official ICA e-Service.',
      },
    ],
  },
  {
    path: 'photo-requirements',
    title: 'Singapore Visa Photo Requirements — Size & Specifications | VisaSG',
    h1: 'Singapore Visa Photo Requirements',
    desc: 'Singapore visa photo specifications per ICA: dimensions, background, expression and quality standards. Follow ICA guidelines to avoid resubmission.',
    intro:
      "Per ICA's photo guidelines, a recent passport-sized colour photograph is required for every visa application. ICA's photo requirements are based on ISO and ICAO specifications.",
    sections: [
      {
        heading: 'Recency',
        body: 'Taken within the last three months. A new coloured photograph must be submitted for each new application. Source: [ICA Photo Guidelines](https://www.ica.gov.sg/photo-guidelines).',
      },
      {
        heading: 'Online Submission — Recommended Dimensions',
        body: '400 × 514 pixels. Source: [ICA Photo Guidelines](https://www.ica.gov.sg/photo-guidelines).',
      },
      {
        heading: 'Online Submission — File Formats and Size',
        body: 'Accepted file formats: JPG, JPEG, HEIC, HEIF, PNG. Maximum file size: 8 MB. Source: [ICA Photo Guidelines](https://www.ica.gov.sg/photo-guidelines).',
      },
      {
        heading: 'Hardcopy Submission — Print Size',
        body: 'For hardcopy submission at a Singapore Overseas Mission, the printed photo is 35 mm × 45 mm on matte or semi-matte (non-reflective) paper.',
      },
      {
        heading: 'Background',
        body: 'Plain white background. Source: [Singapore MFA New Delhi — Visa Information](https://new-delhi.mfa.gov.sg/consular-services/visa-information/).',
      },
      {
        heading: 'Composition & Accessories',
        body: 'Face the camera directly with eyes open; hair must not cover the eyebrows or eyes. Spectacles are permitted if they do not obstruct the eyes or cause lens reflections. Headgear is only allowed for religious or customary reasons and must not obscure facial features. Photographs must not be altered or enhanced (no modification of facial features, no photo-editing software).',
      },
    ],
  },
  {
    path: 'passport-requirements',
    title: 'Singapore Visa Passport Requirements — Validity | VisaSG',
    h1: 'Singapore Visa Passport Requirements',
    desc: 'ICA passport rules for a Singapore entry visa: minimum six-month validity and travel-document conditions confirmed by ICA before you apply.',
    intro:
      'Your passport must meet the conditions below before you can apply for a Singapore visa.',
    sections: [
      {
        heading: 'Six Months Validity',
        body:
          'ICA states: "All travellers, except Singapore passport holders, must ensure their passport has a minimum validity of 6 months." Source: [ICA — Entering Singapore](https://www.ica.gov.sg/enter-transit-depart/entering-singapore).',
      },
      {
        heading: 'Individually-Issued Travel Documents',
        body:
          'ICA requires all travellers, regardless of age, to hold a valid individually-issued travel document; collective travel documents are not accepted.',
      },
      {
        heading: 'Passport Change During Processing',
        body:
          'If your passport details change after you apply, contact VisaSG before travel so we can advise you on the next steps.',
      },
    ],
  },
  {
    path: 'how-to-apply',
    title: 'How to Apply for a Singapore Visa — ICA Channel | VisaSG',
    h1: 'How to Apply for a Singapore Visa',
    desc: 'Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by the Immigration & Checkpoints Authority (ICA). Visa applications are assessed by ICA on their individual merits.',
    intro:
      'Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by the Immigration & Checkpoints Authority (ICA). Visa applications are assessed by ICA on their individual merits. VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers.',
    sections: [
      {
        heading: 'Apply Through the Official ICA Channel',
        body:
          'The official channel for Singapore visa applications is the Immigration & Checkpoints Authority of Singapore. Please refer to the [official ICA visa requirements page](https://www.ica.gov.sg/enter-transit-depart/entering-singapore/visa_requirements) for the current procedure, forms and fees.',
      },
      {
        heading: 'Prescribed ICA Visa Processing Fee',
        body:
          'The prescribed ICA visa processing fee is S$30 per application, payable to ICA through the appropriate channel prescribed by ICA. It is not payable to Waan Travel Pte. Ltd.',
      },
      {
        heading: 'Documents ICA Lists',
        body:
          'The documents ICA lists on its country pages typically include Form 14A, a recent passport-sized colour photograph, a photocopy of your passport bio-data page, and (where applicable per nationality) a Form V39A Letter of Introduction. See [Singapore visa documents required](/singapore-visa/documents/) for a summary.',
      },
      {
        heading: 'Waan Travel Pte. Ltd. — Travel Services',
        body:
          'Waan Travel Pte. Ltd. is a Singapore-licensed travel agency. We can arrange your hotel, flight, tour, transportation and travel itinerary separately from your visa application. Message our team on WhatsApp for a travel-services quote.',
      },
    ],
  },
  {
    path: 'sg-arrival-card',
    title: 'Singapore Visa vs SG Arrival Card — What is Different | VisaSG',
    h1: 'Singapore Visa vs SG Arrival Card',
    desc: 'The Singapore entry visa is a pre-entry permission issued by ICA. The SG Arrival Card is a separate mandatory arrival declaration with ICA-listed exemptions.',
    intro:
      'The Singapore visa and the SG Arrival Card (SGAC) are two separate things. Depending on your nationality and travel status, you may need both. This page explains the difference so you know what to prepare.',
    sections: [
      {
        heading: 'The Singapore Visa',
        body:
          'A Singapore entry visa is pre-entry permission for the holder of a valid visa to travel to and seek entry into Singapore. It does not itself guarantee entry — the grant of an immigration pass is determined by ICA officers at the point of entry. VisaSG helps prepare and lodge visa applications; ICA makes the visa decision. See [Singapore visa requirements by nationality](/singapore-visa/countries/) for who needs a visa.',
      },
      {
        heading: 'The SG Arrival Card (SGAC)',
        body:
          "The SG Arrival Card with Electronic Health Declaration is submitted to ICA before arrival. Per ICA, all travellers must submit the SGAC before arriving in Singapore, except: (a) those transiting or transferring through Singapore without seeking immigration clearance, and (b) Singapore citizens, Permanent Residents and Long-Term Pass holders (Student's Pass, Dependant's Pass, Long-Term Visit Pass, Work Pass) entering via Woodlands and Tuas Checkpoints. The SG Arrival Card is not a Singapore visa. Source: [ICA SG Arrival Card](https://www.ica.gov.sg/enter-transit-depart/entering-singapore/sg-arrival-card).",
      },
      {
        heading: 'When to File the SGAC',
        body:
          "The SGAC must be submitted within three (3) days (including the day of arrival) before arrival in Singapore. For an arrival on 30 June, submissions can begin on 28 June. SGAC submission via the [official ICA SGAC e-Service](https://www.ica.gov.sg/eservicesandforms/sgarrivalcard) is free of charge; VisaSG's optional SGAC filing add-on is a paid convenience service.",
      },
      {
        heading: 'Do You Need Both?',
        body:
          'If your nationality requires a Singapore visa, you need the visa AND (if not exempt) the SGAC. If your nationality is visa-free but you are not exempt from SGAC, you still need to file the SGAC. VisaSG offers an optional SGAC filing add-on during the visa application so both can be handled together.',
      },
      {
        heading: 'Immigration at the Checkpoint',
        body:
          "Neither the visa nor the SGAC guarantees entry. The final decision at the Singapore checkpoint is made by the Immigration Officer at the point of entry. The period of stay granted is determined by the electronic Visit Pass (e-Pass) issued at the checkpoint, and is not tied to the validity of the visa.",
      },
    ],
  },
];

export const SEO_GUIDE_BY_PATH = SEO_GUIDES.reduce((acc, g) => {
  acc[g.path] = g;
  return acc;
}, {});

export const SITE_URL = 'https://visasg.com';
